import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, CreditCard, ShieldCheck, AlertTriangle } from "lucide-react";
import { formatDateTime } from "@/lib/format";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmt(amount, currency = "EUR") {
    try {
        return new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount);
    } catch {
        return `${(amount || 0).toFixed(2)} ${currency}`;
    }
}

export default function PublicPay() {
    const { token } = useParams();
    const [link, setLink] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [paid, setPaid] = useState(false);
    const [name, setName] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND}/api/payments/pay/${token}`);
                setLink(data);
                if (data.status === "paid") setPaid(true);
            } catch (e) {
                setError(e.response?.data?.detail || "Invalid or expired payment link");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setPaying(true);
        try {
            await axios.post(`${BACKEND}/api/payments/pay/${token}/confirm`, { payer_name: name });
            setPaid(true);
        } catch (err) {
            setError(err.response?.data?.detail || "Payment failed");
        } finally {
            setPaying(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500 text-sm" data-testid="public-pay-loading">
                Loading payment…
            </div>
        );
    }

    if (error && !link) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="public-pay-error">
                <div className="max-w-md w-full rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
                    <AlertTriangle className="mx-auto mb-3 text-red-500" size={36} />
                    <div className="text-base font-semibold text-slate-900">Payment link unavailable</div>
                    <div className="mt-1 text-sm text-slate-500">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 px-4 py-10 font-[Inter,sans-serif]">
            <div className="mx-auto max-w-md">
                <div className="mb-6 text-center">
                    <div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-md bg-[#ff7b02] text-white text-sm font-bold">A</div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Akomoda · Secure payment</div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="public-pay-card">
                    <div className="border-b border-slate-200 bg-slate-50/50 px-6 py-4">
                        <div className="text-[11px] uppercase tracking-wider text-slate-500">Amount due</div>
                        <div className="mt-1 text-3xl font-semibold text-slate-900" data-testid="public-pay-amount">
                            {fmt(link.amount, link.currency)}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">{link.description}</div>
                    </div>

                    {paid ? (
                        <div className="px-6 py-10 text-center" data-testid="public-pay-success">
                            <CheckCircle2 className="mx-auto mb-3 text-emerald-600" size={44} />
                            <div className="text-base font-semibold text-slate-900">Payment received</div>
                            <div className="mt-1 text-sm text-slate-500">
                                Thank you. Your reservation has been credited.
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4 px-6 py-5" data-testid="public-pay-form">
                            <div>
                                <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500">
                                    Cardholder name
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Full name on card"
                                    data-testid="public-pay-name"
                                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#ff7b02] focus:ring-1 focus:ring-[#ff7b02]"
                                />
                            </div>
                            <div>
                                <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500">
                                    Card number
                                </label>
                                <div className="mt-1 flex items-center gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-400">
                                    <CreditCard size={14} />
                                    <span className="tracking-widest">•••• •••• •••• 4242</span>
                                    <span className="ml-auto rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-600">demo</span>
                                </div>
                                <div className="mt-1 text-[10px] text-slate-400">
                                    Demo gateway — real Stripe integration coming soon.
                                </div>
                            </div>

                            {error && (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
                            )}

                            <button
                                type="submit"
                                disabled={paying}
                                data-testid="public-pay-submit"
                                className="w-full rounded-md bg-[#ff7b02] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0c5d57] disabled:opacity-60"
                            >
                                {paying ? "Processing…" : `Pay ${fmt(link.amount, link.currency)}`}
                            </button>
                        </form>
                    )}

                    <div className="border-t border-slate-200 bg-slate-50/50 px-6 py-3 text-[11px] text-slate-500 flex items-center gap-2">
                        <ShieldCheck size={12} className="text-emerald-600" />
                        <span>Encrypted &amp; secured by Akomoda</span>
                    </div>
                </div>

                {link.expires_at && (
                    <div className="mt-3 text-center text-[11px] text-slate-400">
                        Expires {formatDateTime(link.expires_at)}
                    </div>
                )}
            </div>
        </div>
    );
}
